import Head from "next/head"
import DefaultLayout from "@/components/DefaultLayout"
import MyAccountLayout from "@/components/MyAccountLayout"
import AddressForm from "@/components/AddressForm"
import { useState, useEffect } from "react"
import Link from 'next/link'
import { Button, Modal, message, Spin, Empty } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import BtnAddAddress from '@/svgs/btn-AddAddress.svg'
import EditIcon from '@/svgs/edit-hover.svg'
import DeleteIcon from '@/svgs/delete-hover.svg'
import styles from "@/styles/myaccount/address.module.scss"
import myAccountApi from '@/service/modules/myAccountApi'

export default function MyaccountAddress() {

  interface addressDataInterface {
    address: string,
    address1: string,
    addressID: number,
    firstName: string,
    lastName: string,
    phone: string,
    postCode: string,
    suburb: string,
    suburbID: string | number,
    city: string,
    isShippingAddress: boolean,
    isBillingAddress: boolean
  }

  interface Address {
    address: string,
    address1: string,
    addressID: number,
    firstName: string,
    lastName: string,
    phone: string,
    postCode: string,
    city: string,
    suburb: string,
    suburbID: string | number
  }

  // 获取地址列表
  const [addressesList, setAddressesList] = useState<addressDataInterface[]>([])
  const getCustomersAddresses = async () => {
    setLoading(true)
    try {
      let res = await myAccountApi.customerAddresses()
      if (res.data) {
        setAddressesList(res.data)
        setLoading(false)
      }
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }
  useEffect(() => {
    getCustomersAddresses()
  }, [])

  // 删除地址
  const handleDeleteAddress = (id: string) => {
    if (!id) return

    const { confirm } = Modal;
    confirm({
      title: 'Delet address',
      content: 'Do you really want to delete this address?',
      async onOk() {
        try {
          let res = await myAccountApi.deleteAddresses(id)
          if (res.data) {
            getCustomersAddresses()
          }
        } catch (error) {
          console.error(error)
        }
      },
      onCancel() {
        console.log('Cancel')
      },
    })
  }

  // 新增或添加地址
  const [currentAddress, setCurrentAddress] = useState<Address | null>(null)
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const showAddressForm = (id?: string) => {
    if (id) {
      // 编辑地址
      let targrt = addressesList.filter(item => Number(item.addressID) === Number(id))[0]

      const data = {
        addressID: targrt.addressID,
        firstName: targrt.firstName,
        lastName: targrt.lastName,
        phone: targrt.phone,
        address: targrt.address,
        address1: targrt.address1,
        suburb: targrt.suburb,
        suburbID: targrt.suburbID,
        city: targrt.city,
        postCode: targrt.postCode
      }

      setCurrentAddress({ ...data })
      setShowForm(true)
    } else {
      // 新增地址
      setCurrentAddress(null)
      setShowForm(true)
    }
  }

  // 设置默认Shipping地址
  const setShippingDefault = async (item: any) => {
    // 如果没有id，或已是默认地址  则return
    if (!item.addressID || item.isShippingAddress) return

    try {
      let res = await myAccountApi.setDefaultShippingAddress({ addressID: item.addressID })
      if (res.data) {
        message.success('Success');
        getCustomersAddresses()
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  // 设置默认Billing地址
  const setBillingDefault = async (item: any) => {
    // 如果没有id，或已是默认地址  则return
    if (!item.addressID || item.isBillingAddress) return

    try {
      let res = await myAccountApi.setDefaultBillingAddress({ addressID: item.addressID })
      if (res.data) {
        message.success('Success');
        getCustomersAddresses()
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  // 新增/编辑成功，刷新页面数据
  const handleSuccess = () => {
    setShowForm(false)
    getCustomersAddresses()
  }

  // 取消新增/编辑
  const handleCancel = () => {
    setShowForm(false)
  }

  return (
    <>
      <Head>
        <title>
          Address - My Account - BestDeals.co.nz
        </title>
      </Head>

      <DefaultLayout>
        <MyAccountLayout>
          <div className={styles['my-address']}>
            <div className={styles['title']}>My Address</div>

            <div className={styles['add-new-address']}>
              <Button
                className={styles['add-address-btn']}
                onClick={() => showAddressForm('')}
              >
                <BtnAddAddress className={styles['add-icon']} />
                Add New Address
              </Button>
            </div>

            {
              showForm &&
              <AddressForm
                address={currentAddress}
                success={handleSuccess}
                cancel={handleCancel}
              ></AddressForm>
            }
            <Spin spinning={loading}>
              {
                addressesList && addressesList.length ?
                  <div className={styles['address-list']}>
                    {
                      addressesList.map((item: addressDataInterface) => (
                        <div className={styles['address-item']} key={item.addressID}>
                          <div className={styles['address-info']}>
                            <div className={styles['info-name']}>{item.firstName} {item.lastName}</div>
                            <div className={styles['info-item']}>{item.suburb} {item.city} {item.postCode} {'New Zealand'}</div>
                            <div className={styles['info-item']}>{item.address} {item.address1}</div>
                            <div className={styles['info-item']}>Phone: {item.phone}</div>
                          </div>
                          <div className={styles['address-operate']}>
                            <div
                              className={`${styles['address-default']}`}
                              onClick={() => setShippingDefault(item)}
                            >
                              <div className={`${styles['checkbox']} ${item.isShippingAddress ? styles['checkbox-active'] : ''}`}>
                                {
                                  !!(item.isShippingAddress) &&
                                  <CheckOutlined className={styles['select-icon']} />
                                }
                              </div>
                              {item.isShippingAddress ? 'Default Shipping Address' : 'Set as Default Shipping Address'}
                            </div>

                            <div
                              className={`${styles['address-default']}`}
                              onClick={() => setBillingDefault(item)}
                            >
                              <div className={`${styles['checkbox']} ${item.isBillingAddress ? styles['checkbox-active'] : ''}`}>
                                {
                                  !!(item.isBillingAddress) &&
                                  <CheckOutlined className={styles['select-icon']} />
                                }
                              </div>
                              {item.isBillingAddress ? 'Default Billing Address' : 'Set as Default Billing Address'}
                            </div>

                            <div className={styles['address-delete-edit']}>
                              <div
                                className={styles['address-edit']}
                                onClick={() => showAddressForm(`${item.addressID}`)}
                              >
                                <EditIcon className={styles['icon-edit']} />Edit
                              </div>

                              <div
                                className={styles['address-edit']}
                                onClick={() => handleDeleteAddress(`${item.addressID}`)}
                              >
                                <DeleteIcon className={styles['icon-edit']} />Delete
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                  :
                  <Empty description="" />
              }
            </Spin>
            <div className={styles['continue-shopping']}>
              <Link
                href={'/'}
                passHref
                legacyBehavior
              >
                <a rel="noopener noreferrer">
                  <Button className={styles['continue-shopping-btn']}>Continue Shopping</Button>
                </a>
              </Link>
            </div>

          </div>
        </MyAccountLayout>
      </DefaultLayout>
    </>
  )
}